import { useContext } from 'react'
import { Outlet } from 'react-router'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import type { CustomWindow } from '../utils'

import { StoresContext } from '../provider/StoreProvider'
import { BottomSheetProvider } from '../provider/BottomSheetProvider'
import { ModalProvider } from '../provider/ModalProvider'

import { useIsMobile } from '../hooks/useIsMobile'
import { useWindowResize } from '../hooks/useWindowResize'

import { GlobalComponents } from '../components/global/GlobalComponents'
import { NavigateTab } from '../components/global/NavigateTab'
import { NeedUpdate } from './NeedUpdate'
import { WebAside } from './WebAside'
import { InvalidAccess } from './InvalidAccess'

import { Color } from '../theme/color'

const matchUserAgent = (matcher: RegExp) => {
  return window.navigator.userAgent.match(matcher)
}

export const Layout = observer(({ isError }: { isError?: boolean }) => {
  const {
    commonStore: { invalidAccess },
  } = useContext(StoresContext)

  const isMobile = useIsMobile()
  const isBreak = useWindowResize(1024)

  const needUpdate =
    process.env.NODE_ENV === 'production' &&
    window.location.pathname.includes('app.artipio.com') &&
    !(window as CustomWindow).CheckAppVersion &&
    (matchUserAgent(/iPhone/gi) || matchUserAgent(/Android/gi))

  if (needUpdate) {
    return <NeedUpdate />
  }

  return (
    <Wrap isMobile={isMobile}>
      {!isMobile && !isBreak && (
        <WebSideView>
          <WebAside />
        </WebSideView>
      )}
      <AppView>
        <BottomSheetProvider>
          <ModalProvider>
            {isError || invalidAccess ? <InvalidAccess /> : <Outlet />}
            <NavigateTab />
            <GlobalComponents />
          </ModalProvider>
        </BottomSheetProvider>
      </AppView>
    </Wrap>
  )
})
const Wrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  gap: 40px;
  ${({ isMobile }) =>
    isMobile
      ? ''
      : `
        background-color: ${Color.gray_1};
        > ${AppView} {
          border: 1px solid ${Color.gray_3};
        }
        *::-webkit-scrollbar {
          display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;
    `}
`

const WebSideView = styled.div`
  display: flex;
  align-items: center;
  width: 450px;
  height: 100vh;
`

const AppView = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: calc(100svh);
  max-width: 500px;
  padding-bottom: env(safe-area-inset-bottom);
`
